import { hasCommandModifier, normalizeCombo } from './key-combo'
import {
    DisableRule,
    EffectiveShortcut,
    GLOBAL_SCOPE,
    Layer,
    RegisteredCommand,
    ScopeId,
    ShortcutPreferences,
    UserBinding
} from './shortcut.types'

export interface ResolveContext {
    /** Route screen currently shown (null on routes without data.screen). */
    screen: ScopeId | null
    /** Open modal scopes, bottom to top. */
    modals: Array<ScopeId>
    /** Registered built-in commands keyed by scope. */
    commands: ReadonlyMap<ScopeId, ReadonlyArray<RegisteredCommand>>
    prefs: ShortcutPreferences
    /** Builds the handler for a user-defined action binding. */
    actionRunner: (binding: UserBinding) => (e?: KeyboardEvent) => void
}

/** Candidates per combo, highest priority first. Disabled entries are already removed. */
export type KeyMap = Map<string, Array<EffectiveShortcut>>

export function layerOf (scope: ScopeId): Layer {
    return scope === GLOBAL_SCOPE ? 'global' : 'screen'
}

export function ruleMatches (rule: DisableRule, commandId: string, layer: Layer, screen: ScopeId | null): boolean {
    return (!rule.command || rule.command === commandId) &&
        (!rule.layer || rule.layer === layer) &&
        (!rule.on || rule.on === screen)
}

export function isDisabled (rules: ReadonlyArray<DisableRule>, commandId: string, layer: Layer, screen: ScopeId | null): boolean {
    return rules.some(r => ruleMatches(r, commandId, layer, screen))
}

export function userCommandId (binding: UserBinding): string {
    return `user.${binding.id ?? `${binding.scope}:${binding.keys}`}`
}

/**
 * Builds the effective key map for the current scope stack.
 *
 * Priority per combo (highest first):
 *   1. user bindings on the screen/modal scopes (top modal first)
 *   2. user bindings on global
 *   3. built-in commands on the screen/modal scopes (top modal first)
 *   4. built-in global commands
 * When a modal is open, only the modal's own scope and commands flagged
 * allowInModal survive. Disabled candidates are dropped, so the next layer
 * down takes over (fall-through).
 */
export function buildKeyMap (ctx: ResolveContext): KeyMap {
    const map: KeyMap = new Map()
    const { settings, bindings } = ctx.prefs
    if (!settings.enabled) return map

    const topModal = ctx.modals.length ? ctx.modals[ctx.modals.length - 1] : null
    // Highest first: modals top-down, then the route screen.
    const localScopes: Array<ScopeId> = [...ctx.modals].reverse()
    if (ctx.screen) localScopes.push(ctx.screen)
    const activeScopes = new Set<ScopeId>([GLOBAL_SCOPE, ...localScopes])

    // By id; several instances can share one (e.g. two grids on a screen)
    const allCommands = new Map<string, Array<RegisteredCommand>>()
    for (const scope of activeScopes) {
        for (const cmd of ctx.commands.get(scope) ?? []) allCommands.set(cmd.id, [...(allCommands.get(cmd.id) ?? []), cmd])
    }

    // Built-in commands the user rebound (in an active scope) lose their default keys.
    const rebound = new Set(
        bindings.filter(b => b.commandId && activeScopes.has(b.scope)).map(b => b.commandId as string)
    )

    const push = (entry: EffectiveShortcut) => {
        if (topModal && entry.scope !== topModal && !entry.allowInModal) return
        if (isDisabled(settings.rules, entry.commandId, entry.layer, ctx.screen)) return
        const list = map.get(entry.keys) ?? []
        list.push(entry)
        map.set(entry.keys, list)
    }

    const pushUserBindings = (scope: ScopeId) => {
        for (const b of bindings) {
            if (b.scope !== scope) continue
            const keys = normalizeCombo(b.keys)
            if (!keys) continue
            if (b.commandId) {
                // A rebind keeps the command's own scope/layer (for disable rules and modal gating)
                for (const cmd of allCommands.get(b.commandId) ?? []) {
                    push(toEntry(cmd, keys, cmd.scope, layerOf(cmd.scope), 'user'))
                }
            }
            else if (b.action) {
                push({
                    keys,
                    commandId: userCommandId(b),
                    label: b.label || b.action.type,
                    group: 'custom',
                    scope,
                    layer: layerOf(scope),
                    source: 'user',
                    run: ctx.actionRunner(b),
                    allowInInputs: hasCommandModifier(keys),
                    allowInModal: false
                })
            }
        }
    }

    const pushBuiltIns = (scope: ScopeId) => {
        for (const cmd of ctx.commands.get(scope) ?? []) {
            if (rebound.has(cmd.id)) continue
            for (const raw of cmd.defaultKeys) {
                const keys = normalizeCombo(raw)
                if (keys) push(toEntry(cmd, keys, scope, layerOf(scope), 'default'))
            }
        }
    }

    localScopes.forEach(pushUserBindings)
    pushUserBindings(GLOBAL_SCOPE)
    localScopes.forEach(pushBuiltIns)
    pushBuiltIns(GLOBAL_SCOPE)

    return map
}

function toEntry (cmd: RegisteredCommand, keys: string, scope: ScopeId, layer: Layer, source: 'default' | 'user'): EffectiveShortcut {
    return {
        keys,
        commandId: cmd.id,
        label: cmd.label,
        group: cmd.group,
        scope,
        layer,
        source,
        run: cmd.run,
        when: cmd.when,
        allowInInputs: cmd.allowInInputs ?? hasCommandModifier(keys),
        allowInModal: cmd.allowInModal ?? false
    }
}

/**
 * Picks the command to run for a combo: the first candidate whose runtime
 * gates pass. A failing `when` or the input gate falls through to the next one.
 */
export function pickCandidate (
    candidates: ReadonlyArray<EffectiveShortcut> | undefined,
    typingInField: boolean,
    unscopedModalOpen: boolean = false
): EffectiveShortcut | null {
    if (!candidates) return null
    for (const c of candidates) {
        if (typingInField && !c.allowInInputs) continue
        if (unscopedModalOpen && !c.allowInModal) continue
        if (c.when && !c.when()) continue
        return c
    }
    return null
}

/** One row per combo (the winner, ignoring runtime gates), for the cheat-sheet. */
export function effectiveList (map: KeyMap): Array<EffectiveShortcut> {
    return [...map.values()].map(list => list[0])
}
